import { uniqBy } from 'lodash'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useStore } from '@/store'
import { getModelIconClasses, modelNamePluralize } from '@/utils'
const store = useStore()

const baseSections = {
  consumables: {
    label: 'Consumables',
    iconClasses: getModelIconClasses('_', 'consumables'),
    models: [['stocks', 'consumable']],
    order: 10,
  },
  equipment: {
    label: 'Equipment',
    iconClasses: getModelIconClasses('_', 'equipment'),
    models: [
      ['stocks', 'instrumentmodel'],
      ['stocks', 'equipment'],
    ],
    order: 20,
  },
  storageequipment: {
    label: 'Storage Equipment',
    iconClasses: getModelIconClasses('_', 'storageequipment'),
    models: [['stocks', 'storageequipment']],
    order: 30,
  },
  specimen: {
    label: 'Specimen',
    iconClasses: getModelIconClasses('_', 'specimen'),
    models: [['stocks', 'specimen']],
    order: 40,
  },
  biomaterials: {
    label: 'Biomaterials',
    iconClasses: getModelIconClasses('_', 'samples'),
    models: [['stocks', 'sample']],
    order: 50,
  },
  protocols: {
    label: 'Protocols',
    iconClasses: getModelIconClasses('_', 'protocols'),
    models: [
      ['protocols', 'protocollist'],
      ['protocols', 'protocol'],
    ],
    order: 60,
  },
  labnotebook: {
    label: 'Lab Notebook',
    iconClasses: getModelIconClasses('_', 'experiments'),
    models: [
      ['core', 'project', 'default'],
      ['assays', 'experiment', 'entry'],
      ['core', 'study', 'default'],
    ],
    order: 70,
  },
  assays: {
    label: 'Assays',
    iconClasses: getModelIconClasses('_', 'assays'),
    models: [
      ['assays', 'assay', 'genericassay'],
      ['assays', 'assay', 'ngsilluminaassay'],
      ['assays', 'assay', 'nanoporeassay'],
      ['assays', 'assay', 'avitisequencingassay'],
      ['assays', 'assay', 'lightmicroscopyassay'],
      ['assays', 'assay', 'lightmicroscopyscreenassay'],
      ['assays', 'assay', 'transmissionemassay'],
      ['assays', 'assay', 'volumeemassay'],
      ['assays', 'instrumentrun', 'default'],
      ['assays', 'assay', 'total'],
      ['assays', 'assay'], // the rest
    ],
    order: 80,
  },
  workflows: {
    label: 'Workflows',
    iconClasses: getModelIconClasses('workflows', '_'),
    models: [
      ['workflows', 'workflow', 'default'],
      ['workflows', 'workflowversion', 'default'],
      ['workflows', 'workflowrun', 'default'],
    ],
    order: 90,
  },
  datasetmanagement: {
    label: 'Dataset Management',
    iconClasses: getModelIconClasses('data_management', '_'),
    models: [
      ['core', 'study', 'default'],
      ['data_management', 'datasession', 'default'],
      ['data_management', 'dataset', 'default'],
      ['data_management', 'datafile', 'default'],
      ['data_management', 'datafilecopy', 'default'],
      ['data_management', 'archive', 'default'],
    ],
    order: 100,
  },
  vocabularies: {
    label: 'Vocabularies',
    iconClasses: getModelIconClasses('vocabularies', '_'),
    hidden: false,
    models: [
      ['annotations', 'annotationtype', 'default'],
      ['vocabularies', 'category', 'default'],
      ['vocabularies', 'term', 'default'],
      ['vocabularies', 'ontology', 'default'],
      ['vocabularies', 'ontologyterm', 'default'],
    ],
    order: 110,
  },
}

// Route-related reactive references
const appName = ref(null)
const modelName = ref(null)
const modelType = ref(null)

export function useMenu() {
  const route = useRoute()
  const routePath = computed(() => route.path)

  watch(
    routePath,
    /* Updates the current route metadata */
    (path) => {
      // eslint-disable-next-line no-useless-escape
      const pathMatch = path.match(/\/([^\/]+)\/([^\/]+)\/([^\/]+)/)
      if (pathMatch) {
        // Extract app name, model name, and model type from the path
        appName.value = pathMatch[1] || null
        modelName.value = pathMatch[2] || null
        modelType.value = pathMatch[3] || null
      } else {
        // Reset values if pattern doesn't match
        appName.value = null
        modelName.value = null
        modelType.value = null
      }
    },
    { immediate: true },
  )

  // Computed properties to access store metadata
  const appsGlobal = computed(() => store.getters['meta/metaGlobalApps'])
  const appsModel = computed(() => store.getters['meta/metaModelApps'])

  /**
   * Main sections computed property that builds the navigation menu structure
   * Processes each section defined in baseSections and enriches it with model data
   */
  const sections = computed(() => {
    return Object.entries(baseSections).reduce(
      (enhancedSections, [sectionKey, sectionInfo]) => {
        // Process each model in the section
        const sectionModels = sectionInfo.models.reduce(
          (enhancedModels, [appName, modelName, modelType]) => {
            let sectionModels = []
            modelType = modelType?.toUpperCase()

            // Get model metadata from store
            const globalModels = appsGlobal.value[appName] || {}
            const modelNameModels =
              (appsModel.value[appName] &&
                appsModel.value[appName][modelName]) ||
              {}

            if (!modelType) {
              // If no specific model type is provided, get all available types
              const modelTypes = Object.keys(modelNameModels)
                // Sort by menu_order if available
                .sort((a, b) => {
                  const o1 = modelNameModels[a]?.menu_order
                  const o2 = modelNameModels[b]?.menu_order
                  return o1 > o2 ? +1 : -1
                })
                // Create menu entries for each type
                .map((t) => ({
                  meta: [appName, modelName, t],
                  url: `${[appName, modelNamePluralize(modelName), t]
                    .join('/')
                    .toLowerCase()}`,
                  // Use default label or specific type label
                  label:
                    t === 'DEFAULT'
                      ? globalModels[modelName]?.verbose_label
                      : modelNameModels[t]?.label,
                  domain: modelNameModels[t]?.domain,
                }))
              sectionModels.push(...modelTypes)
            } else {
              // For specific model types, create a single menu entry
              sectionModels.push({
                meta: [appName, modelName, modelType],
                url: `${[appName, modelNamePluralize(modelName), modelType]
                  .join('/')
                  .toLowerCase()}`,
                label:
                  modelType === 'DEFAULT'
                    ? globalModels[modelName]?.verbose_label
                    : modelNameModels[modelType]?.label,
                domain: modelNameModels[modelType]?.domain,
              })
            }
            enhancedModels.push(...sectionModels)
            return enhancedModels
          },
          [],
        )

        // Return enhanced section with processed models
        return {
          ...enhancedSections,
          [sectionKey]: {
            ...sectionInfo,
            models: uniqBy(sectionModels, 'url'), // Remove any duplicate URLs
          },
        }
      },
      {},
    )
  })

  /**
   * Returns section keys ordered by their defined order property
   * Used to maintain consistent menu ordering
   */
  const orderedSectionsKeys = computed(() => {
    return Object.entries(sections.value)
      .map(([key, { order }]) => ({
        key,
        order,
      }))
      .sort((a, b) => (a.order < b.order ? -1 : +1))
      .map(({ key }) => key)
  })

  const orderedSections = computed(() => {
    // Return sections ordered by their defined order property
    return orderedSectionsKeys.value.map((k) => ({
      key: k,
      ...sections.value[k],
    }))
  })

  /**
   * Creates a mapping of URLs to their corresponding menu items
   * Used for quick lookups and navigation state management
   */
  const urlModelMap = computed(() => {
    const urlMap = {}
    const sectionKeys = Object.keys(sections.value)

    // Build mapping for each model in each section
    for (const key of sectionKeys) {
      const section = sections.value[key]
      const { label, models } = section
      for (const model of models) {
        const [appName, modelName, modelType] = model.meta
        const url = `${appName}/${modelNamePluralize(
          modelName,
        )}/${modelType}`.toLowerCase()
        urlMap[url] = {
          section: key,
          sectionLabel: label,
          meta: [appName, modelName, modelType],
          label: model.label,
        }
      }
    }
    return urlMap
  })

  /**
   * Returns the current subsection based on active route
   * Used to highlight active menu items and maintain navigation state
   */
  const currentSubsection = computed(() => {
    const urlMapKey =
      `${appName.value}/${modelName.value}/${modelType.value}`.toLowerCase()
    const entry = urlModelMap.value[urlMapKey] || {}
    return Object.keys(entry).length ? { ...entry, url: urlMapKey } : {}
  })

  return {
    appName,
    modelName,
    modelType,
    menuSections: orderedSections,
    orderedMenuSections: orderedSections,
    currentMenuSubsection: currentSubsection,
    currentSubsection,
  }
}
